import { useTranslation } from "react-i18next";
import { useCallback, useRef, useState } from "react";
import { Loader2, Scissors, X } from "lucide-react";
import { Dialog, Label, Modal, ModalOverlay, Slider, SliderThumb, SliderTrack, TextField } from "react-aria-components";
import type { ManualMediaRef } from "@arc/contracts";
import { MAX_VIDEO_CUT_SECONDS } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/** `123.4s` → `2:03` (or `1:02:03` past an hour). Whole-second display — the trim runs on whole seconds (`step={1}`). */
function fmt(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  return `${h > 0 ? `${h}:` : ""}${mm}:${String(sec).padStart(2, "0")}`;
}

/** The source name without its extension → the default cut label (the server appends `.mp4` + canonicalizes). */
function baseName(name: string | null | undefined): string {
  return (name ?? "").replace(/\.[^./\\]+$/, "").trim();
}

/**
 * The video TRIM editor (ADR-025 A4 — the trim UI). For an editable VIDEO step, replaces the image actions with a
 * legacy-parity cutter: the `<video>` player + a two-handle timeline (start / end) over the clip's real duration, a name
 * field, and Save/Cancel. Save posts the window to the ASYNC cut command (`onCut`) — the worker trims it and the API lands
 * the result on the step later (no poll/WS yet, owner decision), so on success we just close and let the manual refresh.
 * The cut ALWAYS lands on this step in the current model (the legacy "assign to step" toggle maps to the deferred
 * library-only mode, ADR D5) — shown as a static note rather than a control that can't yet be turned off.
 */
export function VideoTrimEditor({
  media,
  onClose,
  onSaved,
  onCut,
}: {
  media: ManualMediaRef;
  /** Dismiss without cutting (Cancel / X / backdrop). */
  onClose: () => void;
  /** A cut was queued — the caller drops back to the manual (the trimmed clip appears on the next body refresh). */
  onSaved: () => void;
  /** Queue the async cut for `[startSeconds, startSeconds+durationSeconds)`; resolves on the 202, rejects with a coded error. */
  onCut: (input: { sourceFileId: number; startSeconds: number; durationSeconds: number; name?: string }) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [duration, setDuration] = useState(0);
  const [range, setRange] = useState<[number, number]>([0, 0]);
  const [name, setName] = useState(baseName(media.name));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [start, end] = range;
  const cutLength = Math.max(0, end - start);
  const ready = duration > 0;
  const invalid = !ready || cutLength <= 0 || cutLength > MAX_VIDEO_CUT_SECONDS;

  // On a handle move: seek the player to the handle that changed, so the author sees that exact frame.
  const onRangeChange = useCallback((v: number | number[]) => {
    const next = (Array.isArray(v) ? v : [v, v]) as [number, number];
    setRange((prev) => {
      const v0 = videoRef.current;
      if (v0) v0.currentTime = next[0] !== prev[0] ? next[0] : next[1];
      return next;
    });
  }, []);

  const onLoaded = useCallback(() => {
    const d = videoRef.current?.duration;
    if (d != null && Number.isFinite(d) && d > 0) {
      setDuration(d);
      setRange([0, d]);
    }
  }, []);

  async function handleSave(): Promise<void> {
    if (invalid || saving) return;
    setError(null);
    setSaving(true);
    try {
      await onCut({ sourceFileId: media.fileId, startSeconds: Math.round(start), durationSeconds: Math.round(cutLength), name: name.trim() || undefined });
      onSaved();
    } catch (e) {
      const err = e as { code?: string; message?: string };
      setError(
        err.code === "CUT_QUOTA_EXCEEDED"
          ? t("manualEditor.tooManyCuts")
          : err.code === "CUT_SOURCE_NOT_CURRENT"
            ? t("manualEditor.videoChanged")
            : (err.message ?? t("manualEditor.cutQueueFailed")),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalOverlay isDismissable isOpen onOpenChange={(o) => !o && onClose()} className="fixed inset-0 z-[70] flex flex-col bg-black/80 p-4 sm:p-6">
      <Modal className="mx-auto flex min-h-0 w-full max-w-4xl flex-1 flex-col outline-hidden">
        <Dialog aria-label={t("manualEditor.trimVideoOf", { name: media.name ?? t("manualEditor.videoFallback") })} className="flex min-h-0 flex-1 flex-col outline-none">
          <div className="mb-3 flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-white">
              <Scissors aria-hidden="true" className="size-4" />
              {t("manualEditor.trimVideo")}
            </span>
            <Button variant="ghost" size="icon-lg" aria-label={t("manualEditor.close")} onPress={onClose} className="text-white hover:bg-white/15 hover:text-white">
              <X aria-hidden="true" />
            </Button>
          </div>

          {/* The player. */}
          <div className="flex min-h-0 flex-1 items-center justify-center">
            {media.url ? (
              <video ref={videoRef} src={media.url} controls preload="metadata" onLoadedMetadata={onLoaded} className="max-h-full max-w-full rounded-md bg-black" />
            ) : (
              <p className="text-white/80">{t("manualEditor.videoUnsupported")}</p>
            )}
          </div>

          {/* Trim controls. */}
          <div className="bg-card text-card-foreground border-border mt-4 space-y-4 rounded-lg border p-4">
            <div className="flex items-center justify-between text-sm tabular-nums">
              <span className="text-muted-foreground">{t("manualEditor.start")} <span className="text-foreground font-medium">{fmt(start)}</span></span>
              <span className="text-muted-foreground">{t("manualEditor.length")} <span className="text-foreground font-medium">{fmt(cutLength)}</span></span>
              <span className="text-muted-foreground">{t("manualEditor.end")} <span className="text-foreground font-medium">{fmt(end)}</span></span>
            </div>

            {/* Two-handle timeline (react-aria Slider — keyboard-operable). Disabled until the duration is known. */}
            <Slider
              aria-label={t("manualEditor.trimRange")}
              value={range}
              onChange={onRangeChange}
              minValue={0}
              maxValue={ready ? duration : 1}
              step={1}
              isDisabled={!ready}
              className="w-full py-1"
            >
              <SliderTrack className="relative h-6 w-full">
                {({ state }) => {
                  const l = state.getThumbPercent(0) * 100;
                  const r = state.getThumbPercent(1) * 100;
                  return (
                    <>
                      <div className="bg-border absolute top-1/2 h-1.5 w-full -translate-y-1/2 rounded-full" />
                      <div className="bg-primary absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full" style={{ left: `${l}%`, width: `${Math.max(0, r - l)}%` }} />
                      {[0, 1].map((i) => (
                        <SliderThumb
                          key={i}
                          index={i}
                          className="border-background bg-primary data-[focus-visible]:ring-ring/50 top-1/2 size-4 rounded-full border-2 shadow outline-none data-[focus-visible]:ring-2"
                          aria-label={i === 0 ? t("manualEditor.startHandle") : t("manualEditor.endHandle")}
                        />
                      ))}
                    </>
                  );
                }}
              </SliderTrack>
            </Slider>

            <TextField value={name} onChange={setName} className="flex flex-col gap-1.5">
              <Label className="text-foreground text-sm font-medium">{t("common.name")}</Label>
              <Input placeholder={t("manualEditor.cutNamePlaceholder", { base: baseName(media.name) || t("manualEditor.videoFallback") })} />
            </TextField>

            <p className="text-muted-foreground text-xs">{t("manualEditor.cutHint")}</p>

            {error ? (
              <p role="alert" className="text-destructive text-sm">{error}</p>
            ) : null}

            <div className="flex items-center justify-end gap-2 pt-1">
              <Button variant="ghost" onPress={onClose} isDisabled={saving}>
                {t("manualEditor.cancel")}
              </Button>
              <Button onPress={() => void handleSave()} isDisabled={invalid || saving} className="gap-1.5">
                {saving ? <Loader2 aria-hidden="true" className={cn("size-4 animate-spin")} /> : <Scissors aria-hidden="true" className="size-4" />}
                {saving ? t("manualEditor.queuing") : t("manualEditor.saveCut")}
              </Button>
            </div>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
